import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { usePermissions } from "@/lib/permissions";
import { driverService, vehicleService } from "@/services/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SkeletonTable } from "@/components/common/Skeletons";
import { NoResults } from "@/components/common/EmptyState";
import { relativeTime, fmtKm } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, ChevronRight, Plus, RefreshCw, Trash2 } from "lucide-react";
import { AddDriverDialog } from "@/components/dialogs/AddDriverDialog";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { toast } from "sonner";

const DRIVER_STATUS = {
  "on-trip": { label: "On trip", cls: "bg-emerald-400/10 text-emerald-700 dark:text-emerald-300 border-emerald-400/20" },
  available: { label: "Available", cls: "bg-sky-400/10 text-sky-700 dark:text-sky-300 border-sky-400/20" },
  "off-duty": { label: "Off duty", cls: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20" },
};

export function DriverStatusBadge({ status }) {
  const s = DRIVER_STATUS[status] || DRIVER_STATUS["off-duty"];
  return <span className={cn("inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium", s.cls)}>{s.label}</span>;
}

function scoreColor(n) {
  return n == null ? "text-muted-foreground" : n >= 95 ? "text-emerald-600 dark:text-emerald-400" : n >= 85 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400";
}

export default function Drivers() {
  const navigate = useNavigate();
  const { isManager } = usePermissions();
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { data: drivers, loading, refetch } = useFetch(() => driverService.list(), []);

  const list = (drivers || []).filter((d) => !q || d.name.toLowerCase().includes(q.toLowerCase()));

  const removeDriver = async () => {
    try {
      await driverService.remove(deleteTarget.id);
      toast.success("Driver deleted");
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not delete driver");
    } finally {
      setDeleteTarget(null);
    }
  };

  const recalculateScores = async () => {
    setRecalculating(true);
    try {
      const result = await driverService.recalculateScores();
      toast.success(`Recalculated safety scores for ${result.updated} driver${result.updated === 1 ? "" : "s"}`);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not recalculate scores");
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Drivers"
        subtitle="Driver assignments, activity and safety performance."
        actions={isManager && (
          <>
            <Button variant="secondary" disabled={recalculating} onClick={recalculateScores} data-testid="recalculate-scores">
              <RefreshCw className={cn("mr-1.5 h-4 w-4", recalculating && "animate-spin")} /> Recalculate Scores
            </Button>
            <Button onClick={() => setAddOpen(true)} data-testid="add-driver"><Plus className="mr-1.5 h-4 w-4" /> Add Driver</Button>
          </>
        )}
      />
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search drivers…" className="h-10 pl-9" data-testid="drivers-search" />
      </div>

      {loading ? <SkeletonTable rows={6} /> : list.length === 0 ? <NoResults title="No drivers found" /> : (
        <div className="overflow-hidden rounded-2xl border border-border/70 bg-card/40">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border/60">
                <TableHead>Driver</TableHead>
                <TableHead>Assigned Vehicle</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Trips Today</TableHead>
                <TableHead>Distance Today</TableHead>
                <TableHead>Safety Events</TableHead>
                <TableHead>Safety Score</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((d) => {
                const veh = d.assignedVehicleId ? vehicleService.byId(d.assignedVehicleId) : null;
                return (
                  <TableRow key={d.id} onClick={() => navigate(`/drivers/${d.id}`)} className="cursor-pointer border-border/50" data-testid={`driver-row-${d.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9 border border-border/60"><AvatarFallback className="bg-secondary text-xs font-semibold">{d.initials}</AvatarFallback></Avatar>
                        <div><div className="font-medium">{d.name}</div><div className="text-xs text-muted-foreground">{d.email}</div></div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{veh ? veh.name : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell><DriverStatusBadge status={d.status} /></TableCell>
                    <TableCell className="font-telemetry text-sm">{d.tripsToday}</TableCell>
                    <TableCell className="font-telemetry text-sm">{fmtKm(d.distanceToday)}</TableCell>
                    <TableCell><span className={cn("font-telemetry text-sm", d.safetyEvents > 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground")}>{d.safetyEvents}</span></TableCell>
                    <TableCell><span className={cn("font-telemetry text-sm font-semibold", scoreColor(d.safetyScore))}>{d.safetyScore ?? "—"}</span></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{relativeTime(d.lastActive)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {isManager && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(d); }}
                            className="rounded-md p-1 text-muted-foreground hover:bg-red-500/10 hover:text-red-400"
                            data-testid={`driver-delete-${d.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <AddDriverDialog open={addOpen} onOpenChange={setAddOpen} onSaved={refetch} />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete driver?"
        description={`This will permanently remove "${deleteTarget?.name}". Their past trips stay on record but will no longer show a driver name.`}
        confirmLabel="Delete"
        destructive
        onConfirm={removeDriver}
      />
    </div>
  );
}
